import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';

import { VIRTUOUS_STAGED_ATTRIBUTES } from '@feathr/blackbox';
import type { IIntegrationUITab } from '@feathr/components';
import {
  IntegrationActionMenu,
  IntegrationExternalLink,
  IntegrationPageShell,
  IntegrationSetupSteps,
  noop,
} from '@feathr/components';
import { IntegrationPageFrame } from '@feathr/extender/App/Page';
import {
  useCrmConnectionControls,
  useCrmSave,
  useCrmSyncSummary,
} from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmIntegrationPage.hooks';
import {
  useCrmMappingDraft,
  useCrmMappingsRefresh,
} from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmMappings.hooks';
import { getHeaderLastSyncedAt } from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmSyncSummary.utils';
import { useRaisersEdgePollCount as usePollCount } from '@feathr/extender/App/Settings/Account/RaisersEdgeIntegrationPageV2/RaisersEdgeIntegrationPageV2.hooks';
import {
  getBusyReason,
  getUnsavedChangesPrompt,
} from '@feathr/extender/App/Settings/Account/RaisersEdgeIntegrationPageV2/RaisersEdgeIntegrationPageV2.utils';
import { useLocalUrl, useStore } from '@feathr/extender/state';

import VirtuousActivitiesTab from './VirtuousActivitiesTab';
import {
  CONTACT_SYNC_SECTION_KEY,
  CREATE_CONTACTS_SECTION_KEY,
  getVirtuousConfig,
} from './virtuousConfig';
import VirtuousConnectModal from './VirtuousConnectModal';
import { useVirtuousConnect } from './VirtuousIntegrationPage.hooks';
import { getMissingOptionReason } from './VirtuousIntegrationPage.utils';
import VirtuousMappingsTab from './VirtuousMappingsTab';
import VirtuousSettingsTab from './VirtuousSettingsTab';

const MAPPING_RULES = { requireFeathrField: true };

const HELP_ARTICLE_URL =
  'https://help.feathr.co/hc/en-us/articles/26647119300759-How-to-Integrate-Feathr-and-Virtuous';

function VirtuousIntegrationPage(): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const { VirtuousIntegrations } = useStore();

  const integrations = VirtuousIntegrations.list();
  // An account has at most one integration. Check length first: MobX warns on an out-of-bounds read.
  const integration = integrations.models.length ? integrations.models[0] : undefined;
  const config = useMemo(() => getVirtuousConfig(t), [t]);
  const basePath = localUrl('/settings/integrations/virtuous');
  const mappingDraft = useCrmMappingDraft(integration, config.crmName, MAPPING_RULES);
  const { handleSave, isDirty, isSaving, saveDisabledReason } = useCrmSave(
    integration,
    mappingDraft,
    {
      getSaveDisabledReason: (model) => getMissingOptionReason(model, t),
      validatedAttributes: [],
    },
  );
  const { handleDisconnect, handlePause, handleResume, isChangingState } = useCrmConnectionControls(
    integration,
    { stagedAttributes: VIRTUOUS_STAGED_ATTRIBUTES },
  );
  const connection = useVirtuousConnect();
  const unsavedChangesPrompt = useMemo(() => getUnsavedChangesPrompt(basePath, t), [basePath, t]);
  const busyReason = getBusyReason({ isChangingState, isSaving }, t);
  const menuDisabledReasons = busyReason
    ? { disconnect: busyReason, pause: busyReason, resume: busyReason }
    : undefined;
  const isActive = !!integration?.isActive;
  const pollCount = usePollCount(isActive && !isSaving);
  const syncSummary = useCrmSyncSummary(integration, isSaving, pollCount);
  useCrmMappingsRefresh(integration, pollCount);

  async function handleSetupAction(): Promise<void> {
    connection.openModal();
  }

  function renderTab(tab: IIntegrationUITab): ReactNode {
    if (!integration) {
      return null;
    }
    const sections = config.toggleSections.filter((section) => section.tab === tab.key);

    switch (tab.key) {
      case 'mappings':
        return (
          <VirtuousMappingsTab
            config={config}
            disabledReason={busyReason}
            draft={mappingDraft}
            integration={integration}
            key={integration.id}
            sectionsAbove={sections.filter(({ key }) => key === CONTACT_SYNC_SECTION_KEY)}
            sectionsBelow={sections.filter(({ key }) => key === CREATE_CONTACTS_SECTION_KEY)}
            syncSummary={syncSummary}
          />
        );

      case 'activities':
        return (
          <VirtuousActivitiesTab
            disabledReason={busyReason}
            integration={integration}
            key={integration.id}
            sections={sections}
          />
        );

      case 'settings':
        return (
          <VirtuousSettingsTab
            disabledReason={busyReason}
            integration={integration}
            key={integration.id}
            onDisconnect={handleDisconnect}
          />
        );

      default:
        return null;
    }
  }

  return (
    <>
      <Prompt message={unsavedChangesPrompt} when={isDirty} />
      <IntegrationPageShell
        actionMenu={
          integration && (
            <IntegrationActionMenu
              crmName={config.crmName}
              disabledReasons={menuDisabledReasons}
              name={'virtuous-actions'}
              onDisconnect={handleDisconnect}
              onPause={handlePause}
              onResume={handleResume}
              state={integration.integrationState}
            />
          )
        }
        basePath={basePath}
        config={config}
        connectionState={integration?.integrationState ?? 'not_connected'}
        description={
          <Trans t={t}>
            Sync your Virtuous contacts and gifts with Feathr.{' '}
            <IntegrationExternalLink href={HELP_ARTICLE_URL} name={'virtuous-help-link'}>
              Learn more
            </IntegrationExternalLink>
          </Trans>
        }
        importDisabledReason={t('Import is not available yet.')}
        isDirty={isDirty}
        isSaving={isSaving}
        lastSyncedAt={
          isActive ? getHeaderLastSyncedAt(syncSummary, integration?.lastSynced) : undefined
        }
        loading={integrations.isPending}
        onImport={noop}
        onSave={handleSave}
        PageComponent={IntegrationPageFrame}
        renderTab={renderTab}
        saveDisabledReason={isChangingState ? busyReason : saveDisabledReason}
        setup={
          !isActive && (
            <IntegrationSetupSteps
              completedCount={0}
              loadingKey={connection.isConnecting ? config.setupSteps[0].key : undefined}
              name={'virtuous-setup'}
              onAction={handleSetupAction}
              steps={config.setupSteps}
            />
          )
        }
      />
      <VirtuousConnectModal
        isConnecting={connection.isConnecting}
        onClose={connection.closeModal}
        onConnect={connection.connect}
        opened={connection.isModalOpen}
      />
    </>
  );
}

export default observer(VirtuousIntegrationPage);
